import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useSimulation } from './hooks/useSimulation';
import { MapCanvas } from './components/MapCanvas';
import { ControlPanel } from './components/ControlPanel';
import { CardExclusionModal } from './components/CardExclusionModal';
import { GroupCardSelectionModal } from './components/GroupCardSelectionModal';
import { NPCDetailPanel } from './screens/NPCDetailPanel';
import { WorldStatsPanel } from './screens/WorldStatsPanel';
import { EventLogPanel } from './screens/EventLogPanel';
import { StatsGraphPanel } from './screens/StatsGraphPanel';
import { FamilyTreePanel } from './screens/FamilyTreePanel';
import { FamilyTreeGraphPanel } from './screens/FamilyTreeGraphPanel';
import { CardStatsPanel } from './screens/CardStatsPanel';
import { NPCListPanel } from './screens/NPCListPanel';
import { DeckAnalysisPanel } from './screens/DeckAnalysisPanel';
import { GroupStatsPanel } from './screens/GroupStatsPanel';
import { useIsMobile, useIsTablet } from '../collection/hooks/useMediaQuery';
import type { ExtendedCardData } from '../utils/cardManager';
import type { SimNPC } from '../simulation/types/SimNPC';
import type { GroupConfig } from '../simulation/types/Group';
import { createGroupConfig } from '../simulation/types/Group';
import { isGroupModeEnabled } from '../simulation/types/SimWorld';

// グループカラーのプリセット
const GROUP_COLORS = [
  '#ff6b6b', // 赤
  '#4ecdc4', // シアン
  '#ffd93d', // 黄
  '#6bcb77', // 緑
  '#9d65c9', // 紫
  '#ff8c42', // オレンジ
];

// グループ設定の編集用型
interface GroupSettingEdit {
  name: string;
  allowedCardIds: Set<string>;
  initialNpcCount: number;
  color: string;
  // 非対称進化モード設定
  populationMode: 'fixed' | 'natural';
  fixedPopulation: number;
  internalBattle: boolean;
  externalBattle: boolean;
  recordInternal: boolean;
  recordExternal: boolean;
}

interface SimulationAppProps {
  allCards: ExtendedCardData[];
  onBack?: () => void;
}

export const SimulationApp: React.FC<SimulationAppProps> = ({ allCards, onBack }) => {
  // 除外カードID
  const [excludedCardIds, setExcludedCardIds] = useState<Set<string>>(new Set());
  const [showExclusionModal, setShowExclusionModal] = useState(false);

  // グループモード設定
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [groupModeEnabled, setGroupModeEnabled] = useState(false);
  const [groupCount, setGroupCount] = useState(3);
  // カード選択モーダルを開いているグループのインデックス（-1 = 閉じている）
  const [editingGroupCardIndex, setEditingGroupCardIndex] = useState(-1);

  // 全カードIDのセット（初期値用）
  const allCardIds = useMemo(() => new Set(allCards.map(c => c.id)), [allCards]);

  // 各グループの詳細設定
  const [groupSettings, setGroupSettings] = useState<GroupSettingEdit[]>(() =>
    Array.from({ length: 6 }, (_, i) => ({
      name: `グループ${String.fromCharCode(65 + i)}`,
      allowedCardIds: new Set<string>(), // 初期値は空（後でallCardIdsで初期化される場合）
      initialNpcCount: 3,
      color: GROUP_COLORS[i],
      // 非対称進化モード設定（デフォルト値）
      populationMode: 'natural' as const,
      fixedPopulation: 3,
      internalBattle: true,
      externalBattle: true,
      recordInternal: true,
      recordExternal: true,
    }))
  );

  // allCardsが読み込まれたらグループ設定の初期化
  React.useEffect(() => {
    if (allCards.length > 0) {
      setGroupSettings(prev => prev.map(setting => ({
        ...setting,
        allowedCardIds: setting.allowedCardIds.size === 0 ? new Set(allCards.map(c => c.id)) : setting.allowedCardIds,
      })));
    }
  }, [allCards]);

  const simulation = useSimulation(allCards, {
    initialNPCCount: 6,
    mapWidth: 15,
    mapHeight: 15,
    excludedCardIds,
  });

  const [selectedNPC, setSelectedNPC] = useState<SimNPC | null>(null);
  const [activeTab, setActiveTab] = useState<'stats' | 'npcs' | 'log' | 'family' | 'familyGraph' | 'cards' | 'deckAnalysis' | 'groups' | 'world'>('stats');
  // モバイルでマップ/コンテンツ切り替え用
  const [mobileView, setMobileView] = useState<'map' | 'content'>('map');
  // タブバーのスクロール用ref
  const tabBarRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState({ left: false, right: false });

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // 除外カード設定を保存してリセット
  const handleSaveExclusion = useCallback((newExcludedIds: Set<string>) => {
    setExcludedCardIds(newExcludedIds);
    // 新しい除外設定でリセット
    simulation.resetWithConfig({ excludedCardIds: newExcludedIds });
  }, [simulation]);

  // グループ設定を適用してリセット
  const handleApplyGroupSettings = useCallback(() => {
    if (groupModeEnabled) {
      // カスタムグループ設定から GroupConfig[] を作成
      const groups: GroupConfig[] = groupSettings.slice(0, groupCount).map((setting, i) => {
        const config = createGroupConfig(
          i,
          { allowedCardIds: setting.allowedCardIds },
          setting.initialNpcCount,
          setting.name,
          setting.color
        );
        // 非対称進化モード設定を追加
        if (setting.populationMode === 'fixed') {
          config.populationMode = 'fixed';
          config.fixedPopulation = setting.fixedPopulation;
          // 固定人口モードではリスポーン設定もデフォルト値で追加
          config.respawnConfig = {
            selfCardRatio: 0.5,
            groupCardRatio: 0.3,
            randomCardRatio: 0.2,
          };
        }
        // デフォルトと異なる場合のみバトル設定を追加
        const needsBattleConfig =
          !setting.internalBattle ||
          !setting.externalBattle ||
          !setting.recordInternal ||
          !setting.recordExternal;
        if (needsBattleConfig) {
          config.battleConfig = {
            internalBattle: setting.internalBattle,
            externalBattle: setting.externalBattle,
            recordInternal: setting.recordInternal,
            recordExternal: setting.recordExternal,
          };
        }
        return config;
      });
      simulation.resetWithConfig({
        excludedCardIds,
        groupModeConfig: {
          enabled: true,
          groups,
        },
      });
    } else {
      simulation.resetWithConfig({
        excludedCardIds,
        groupModeConfig: undefined,
      });
    }
    setShowGroupSettings(false);
  }, [groupModeEnabled, groupCount, groupSettings, excludedCardIds, simulation]);

  // グループ設定を更新するヘルパー
  const updateGroupSetting = useCallback((index: number, updates: Partial<GroupSettingEdit>) => {
    setGroupSettings(prev => prev.map((setting, i) =>
      i === index ? { ...setting, ...updates } : setting
    ));
  }, []);

  // グループのカード設定を保存
  const handleSaveGroupCards = useCallback((groupIndex: number, allowedCardIds: Set<string>) => {
    updateGroupSetting(groupIndex, { allowedCardIds });
    setEditingGroupCardIndex(-1);
  }, [updateGroupSetting]);

  // タブ定義
  const tabs = [
    { key: 'stats', label: '📊 統計', shortLabel: '📊' },
    { key: 'npcs', label: '👥 NPCs', shortLabel: '👥' },
    { key: 'family', label: '🌳 家系図', shortLabel: '🌳' },
    { key: 'familyGraph', label: '🗺️ 家系図(グラフ)', shortLabel: '🗺️' },
    { key: 'log', label: '📜 ログ', shortLabel: '📜' },
    { key: 'cards', label: '🃏 カード統計', shortLabel: '🃏' },
    { key: 'deckAnalysis', label: '🔍 デッキ分析', shortLabel: '🔍' },
    ...(isGroupModeEnabled(simulation.world) ? [{ key: 'groups', label: '🏆 グループ', shortLabel: '🏆' }] : []),
    ...(isMobile ? [{ key: 'world', label: '🌍 ワールド', shortLabel: '🌍' }] : []),
  ] as const;

  // タブバーのスクロール状態をチェック
  const checkScrollButtons = useCallback(() => {
    const tabBar = tabBarRef.current;
    if (!tabBar) return;
    const { scrollLeft, scrollWidth, clientWidth } = tabBar;
    setShowScrollButtons({
      left: scrollLeft > 0,
      right: scrollLeft < scrollWidth - clientWidth - 1,
    });
  }, []);

  // タブバーをスクロール
  const scrollTabs = useCallback((direction: 'left' | 'right') => {
    const tabBar = tabBarRef.current;
    if (!tabBar) return;
    const scrollAmount = 150;
    tabBar.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  // タブバーのスクロールイベント監視
  React.useEffect(() => {
    const tabBar = tabBarRef.current;
    if (!tabBar) return;
    checkScrollButtons();
    tabBar.addEventListener('scroll', checkScrollButtons);
    window.addEventListener('resize', checkScrollButtons);
    return () => {
      tabBar.removeEventListener('scroll', checkScrollButtons);
      window.removeEventListener('resize', checkScrollButtons);
    };
  }, [checkScrollButtons, tabs.length]);

  // 動的スタイル
  const getMainContentStyle = (): React.CSSProperties => {
    if (isMobile) {
      return {
        ...styles.mainContent,
        gridTemplateColumns: '1fr',
        gap: '8px',
      };
    }
    if (isTablet) {
      return {
        ...styles.mainContent,
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
      };
    }
    return styles.mainContent;
  };

  const getHeaderStyle = (): React.CSSProperties => {
    if (isMobile) {
      return {
        ...styles.header,
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '8px',
      };
    }
    return styles.header;
  };

  const getTitleStyle = (): React.CSSProperties => {
    if (isMobile) {
      return {
        ...styles.title,
        fontSize: '18px',
      };
    }
    return styles.title;
  };

  const getTabBarStyle = (): React.CSSProperties => {
    return {
      ...styles.tabBar,
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
    };
  };

  const getTabButtonStyle = (isActive: boolean): React.CSSProperties => {
    const base = {
      ...styles.tabButton,
      ...(isActive ? styles.tabButtonActive : {}),
      whiteSpace: 'nowrap' as const,
      flexShrink: 0,
    };
    if (isMobile) {
      return {
        ...base,
        padding: '8px 12px',
        fontSize: '12px',
      };
    }
    return base;
  };

  const getModalContentStyle = (): React.CSSProperties => {
    if (isMobile) {
      return {
        ...styles.modalContent,
        width: 'calc(100% - 16px)',
        maxWidth: 'none',
        left: '8px',
        right: '8px',
        transform: 'translateY(-50%)',
        maxHeight: '90vh',
      };
    }
    return styles.modalContent;
  };

  const getContainerStyle = (): React.CSSProperties => {
    if (isMobile) {
      return {
        ...styles.container,
        padding: '8px',
      };
    }
    return styles.container;
  };

  return (
    <div style={getContainerStyle()}>
      {/* ヘッダー + コントロール */}
      <div style={getHeaderStyle()}>
        <div style={styles.headerLeft}>
          <h1 style={getTitleStyle()}>{isMobile ? 'Life Simulation' : 'Magic Spirit - Life Simulation'}</h1>
          {onBack && (
            <button onClick={onBack} style={styles.backButton}>
              ← {isMobile ? '' : 'Back to Menu'}
            </button>
          )}
          <button
            onClick={() => setShowExclusionModal(true)}
            style={styles.exclusionButton}
            title="カード除外設定"
          >
            🃏 {excludedCardIds.size > 0 ? `(${excludedCardIds.size})` : ''}
          </button>
          <button
            onClick={() => setShowGroupSettings(true)}
            style={{
              ...styles.exclusionButton,
              background: isGroupModeEnabled(simulation.world)
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #4a4a6a 0%, #3a3a5a 100%)',
            }}
            title="グループモード設定"
          >
            🏆 {isGroupModeEnabled(simulation.world) ? 'ON' : 'OFF'}
          </button>
        </div>
        <div style={styles.headerRight}>
          <ControlPanel
            isPlaying={simulation.isPlaying}
            speed={simulation.speed}
            currentDay={simulation.world.day}
            initialNPCCount={simulation.config.initialNPCCount}
            onPlay={simulation.play}
            onPause={simulation.pause}
            onStep={simulation.step}
            onReset={simulation.reset}
            onResetWithNPCCount={(count) => simulation.resetWithConfig({ initialNPCCount: count })}
            onSpeedChange={simulation.setSpeed}
            isMobile={isMobile}
          />
        </div>
      </div>

      {/* モバイル: マップ/コンテンツ切り替えボタン */}
      {isMobile && (
        <div style={styles.mobileViewToggle}>
          <button
            style={{
              ...styles.mobileViewButton,
              ...(mobileView === 'map' ? styles.mobileViewButtonActive : {}),
            }}
            onClick={() => setMobileView('map')}
          >
            🗺️ マップ
          </button>
          <button
            style={{
              ...styles.mobileViewButton,
              ...(mobileView === 'content' ? styles.mobileViewButtonActive : {}),
            }}
            onClick={() => setMobileView('content')}
          >
            📋 コンテンツ
          </button>
        </div>
      )}

      {/* メインコンテンツ */}
      <div style={getMainContentStyle()}>
        {/* 左: マップ (モバイルでは切り替え表示) */}
        {(!isMobile || mobileView === 'map') && (
          <div style={isMobile ? styles.mapColumnMobile : styles.mapColumn}>
            <MapCanvas
              map={simulation.map}
              npcs={simulation.world.npcs}
              onNPCClick={setSelectedNPC}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* 中央: タブコンテンツ (モバイルでは切り替え表示) */}
        {(!isMobile || mobileView === 'content') && (
          <div style={styles.centerColumn}>
            <div style={styles.tabBarContainer}>
              {showScrollButtons.left && (
                <button
                  style={styles.tabScrollButton}
                  onClick={() => scrollTabs('left')}
                  aria-label="Scroll tabs left"
                >
                  ◀
                </button>
              )}
              <div
                ref={tabBarRef}
                style={getTabBarStyle()}
                onWheel={(e) => {
                  if (tabBarRef.current && e.deltaY !== 0) {
                    e.preventDefault();
                    tabBarRef.current.scrollLeft += e.deltaY;
                  }
                }}
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    style={getTabButtonStyle(activeTab === tab.key)}
                    onClick={() => setActiveTab(tab.key as typeof activeTab)}
                  >
                    {isMobile ? tab.shortLabel : tab.label}
                  </button>
                ))}
              </div>
              {showScrollButtons.right && (
                <button
                  style={styles.tabScrollButton}
                  onClick={() => scrollTabs('right')}
                  aria-label="Scroll tabs right"
                >
                  ▶
                </button>
              )}
            </div>

            <div style={styles.tabContent}>
              {activeTab === 'stats' && <StatsGraphPanel world={simulation.world} onNPCClick={setSelectedNPC} isMobile={isMobile} />}
              {activeTab === 'npcs' && <NPCListPanel world={simulation.world} onNPCClick={setSelectedNPC} isMobile={isMobile} />}
              {activeTab === 'family' && (
                <FamilyTreePanel world={simulation.world} onNPCClick={setSelectedNPC} isMobile={isMobile} />
              )}
              {activeTab === 'familyGraph' && (
                <FamilyTreeGraphPanel world={simulation.world} onNPCClick={setSelectedNPC} isMobile={isMobile} />
              )}
              {activeTab === 'log' && <EventLogPanel world={simulation.world} isMobile={isMobile} />}
              {activeTab === 'cards' && <CardStatsPanel world={simulation.world} onNPCClick={setSelectedNPC} isMobile={isMobile} />}
              {activeTab === 'deckAnalysis' && <DeckAnalysisPanel world={simulation.world} onNPCClick={setSelectedNPC} isMobile={isMobile} />}
              {activeTab === 'groups' && isGroupModeEnabled(simulation.world) && (
                <GroupStatsPanel world={simulation.world} isMobile={isMobile} />
              )}
              {activeTab === 'world' && isMobile && <WorldStatsPanel world={simulation.world} onNPCClick={setSelectedNPC} isMobile={isMobile} />}
            </div>
          </div>
        )}

        {/* 右: ワールド統計 (モバイルではタブに統合) */}
        {!isMobile && (
          <div style={styles.rightColumn}>
            <WorldStatsPanel world={simulation.world} onNPCClick={setSelectedNPC} isMobile={false} />
          </div>
        )}
      </div>

      {/* NPCデータルパネル（モーダル表示） */}
      {selectedNPC && (
        <>
          <div style={styles.modalOverlay} onClick={() => setSelectedNPC(null)} />
          <div style={getModalContentStyle()}>
            <NPCDetailPanel
              npc={selectedNPC}
              allNPCs={simulation.world.npcs}
              onClose={() => setSelectedNPC(null)}
              onNPCClick={setSelectedNPC}
              isMobile={isMobile}
              groupConfig={
                selectedNPC.group && simulation.world.groupModeConfig?.groups
                  ? simulation.world.groupModeConfig.groups.find(g => g.id === selectedNPC.group)
                  : undefined
              }
            />
          </div>
        </>
      )}

      {/* カード除外設定モーダル */}
      {showExclusionModal && (
        <CardExclusionModal
          allCards={allCards}
          excludedCardIds={excludedCardIds}
          onSave={handleSaveExclusion}
          onClose={() => setShowExclusionModal(false)}
          isMobile={isMobile}
        />
      )}

      {/* グループ設定モーダル */}
      {showGroupSettings && (
        <>
          <div style={styles.modalOverlay} onClick={() => setShowGroupSettings(false)} />
          <div style={{
            ...getModalContentStyle(),
            background: '#1a1a2e',
            borderRadius: '12px',
            padding: '24px',
            border: '2px solid #444',
            minWidth: isMobile ? 'auto' : '600px',
            maxWidth: isMobile ? 'calc(100% - 16px)' : '800px',
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#fff', fontSize: '20px' }}>
              🏆 グループモード設定
            </h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={groupModeEnabled}
                  onChange={(e) => setGroupModeEnabled(e.target.checked)}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <span style={{ color: '#fff', fontSize: '16px' }}>グループモードを有効にする</span>
              </label>
            </div>

            {groupModeEnabled && (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#aaa' }}>
                    グループ数（2〜6）
                  </label>
                  <input
                    type="range"
                    min={2}
                    max={6}
                    value={groupCount}
                    onChange={(e) => setGroupCount(parseInt(e.target.value))}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                  <div style={{ textAlign: 'center', color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>
                    {groupCount} グループ
                  </div>
                </div>

                {/* 各グループの設定 */}
                <div style={{ marginBottom: '20px', maxHeight: '400px', overflowY: 'auto' }}>
                  {groupSettings.slice(0, groupCount).map((setting, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: '16px',
                        padding: '16px',
                        background: '#2a2a3e',
                        borderRadius: '8px',
                        border: `2px solid ${setting.color}`,
                      }}
                    >
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        {/* グループ名 */}
                        <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', color: '#888', fontSize: '12px' }}>
                            グループ名
                          </label>
                          <input
                            type="text"
                            value={setting.name}
                            onChange={(e) => updateGroupSetting(index, { name: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '8px',
                              fontSize: '14px',
                              borderRadius: '4px',
                              border: '1px solid #444',
                              background: '#1a1a2e',
                              color: '#fff',
                            }}
                          />
                        </div>

                        {/* NPC数 */}
                        <div style={{ width: '80px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', color: '#888', fontSize: '12px' }}>
                            NPC数
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={60}
                            value={setting.initialNpcCount}
                            onChange={(e) => updateGroupSetting(index, {
                              initialNpcCount: Math.min(60, Math.max(1, parseInt(e.target.value) || 1))
                            })}
                            style={{
                              width: '100%',
                              padding: '8px',
                              fontSize: '14px',
                              borderRadius: '4px',
                              border: '1px solid #444',
                              background: '#1a1a2e',
                              color: '#fff',
                            }}
                          />
                        </div>

                        {/* カラー */}
                        <div style={{ width: '60px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', color: '#888', fontSize: '12px' }}>
                            色
                          </label>
                          <input
                            type="color"
                            value={setting.color}
                            onChange={(e) => updateGroupSetting(index, { color: e.target.value })}
                            style={{
                              width: '100%',
                              height: '32px',
                              padding: '0',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          />
                        </div>
                      </div>

                      {/* カード設定 */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', color: '#888', fontSize: '12px' }}>
                          使用可能カード
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <button
                            onClick={() => setEditingGroupCardIndex(index)}
                            style={{
                              padding: '8px 16px',
                              fontSize: '13px',
                              borderRadius: '6px',
                              border: `2px solid ${setting.color}`,
                              background: 'transparent',
                              color: setting.color,
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              transition: 'all 0.2s',
                            }}
                          >
                            カード設定...
                          </button>
                          <span style={{ color: '#aaa', fontSize: '12px' }}>
                            {setting.allowedCardIds.size} / {allCards.length} 枚選択中
                          </span>
                        </div>
                        {setting.allowedCardIds.size === 0 && (
                          <div style={{ color: '#f44336', fontSize: '12px', marginTop: '4px' }}>
                            ⚠️ 少なくとも1枚のカードを選択してください
                          </div>
                        )}
                      </div>

                      {/* 非対称進化モード設定 */}
                      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #444' }}>
                        <label style={{ display: 'block', marginBottom: '12px', color: '#888', fontSize: '12px' }}>
                          進化モード設定
                        </label>

                        {/* 人口モード */}
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`popMode-${index}`}
                                checked={setting.populationMode === 'natural'}
                                onChange={() => updateGroupSetting(index, { populationMode: 'natural' })}
                                style={{ cursor: 'pointer' }}
                              />
                              <span style={{ color: '#ccc', fontSize: '13px' }}>自然増減</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`popMode-${index}`}
                                checked={setting.populationMode === 'fixed'}
                                onChange={() => updateGroupSetting(index, {
                                  populationMode: 'fixed',
                                  fixedPopulation: setting.initialNpcCount,
                                })}
                                style={{ cursor: 'pointer' }}
                              />
                              <span style={{ color: '#ccc', fontSize: '13px' }}>固定人口</span>
                            </label>
                            {setting.populationMode === 'fixed' && (
                              <input
                                type="number"
                                min={1}
                                max={60}
                                value={setting.fixedPopulation}
                                onChange={(e) => updateGroupSetting(index, {
                                  fixedPopulation: Math.min(60, Math.max(1, parseInt(e.target.value) || 1))
                                })}
                                style={{
                                  width: '60px',
                                  padding: '4px 8px',
                                  fontSize: '13px',
                                  borderRadius: '4px',
                                  border: '1px solid #444',
                                  background: '#1a1a2e',
                                  color: '#fff',
                                }}
                              />
                            )}
                          </div>
                        </div>

                        {/* 戦闘設定 */}
                        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ color: '#888', fontSize: '11px', marginBottom: '6px' }}>戦闘タイプ</div>
                            <div style={{ display: 'flex', gap: '12px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={setting.internalBattle}
                                  onChange={(e) => updateGroupSetting(index, { internalBattle: e.target.checked })}
                                  style={{ cursor: 'pointer' }}
                                />
                                <span style={{ color: '#ccc', fontSize: '12px' }}>内部戦</span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={setting.externalBattle}
                                  onChange={(e) => updateGroupSetting(index, { externalBattle: e.target.checked })}
                                  style={{ cursor: 'pointer' }}
                                />
                                <span style={{ color: '#ccc', fontSize: '12px' }}>外部戦</span>
                              </label>
                            </div>
                          </div>
                          <div>
                            <div style={{ color: '#888', fontSize: '11px', marginBottom: '6px' }}>進化に反映</div>
                            <div style={{ display: 'flex', gap: '12px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={setting.recordInternal}
                                  onChange={(e) => updateGroupSetting(index, { recordInternal: e.target.checked })}
                                  style={{ cursor: 'pointer' }}
                                  disabled={!setting.internalBattle}
                                />
                                <span style={{ color: setting.internalBattle ? '#ccc' : '#666', fontSize: '12px' }}>内部戦</span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={setting.recordExternal}
                                  onChange={(e) => updateGroupSetting(index, { recordExternal: e.target.checked })}
                                  style={{ cursor: 'pointer' }}
                                  disabled={!setting.externalBattle}
                                />
                                <span style={{ color: setting.externalBattle ? '#ccc' : '#666', fontSize: '12px' }}>外部戦</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ marginBottom: '16px', padding: '12px', background: '#2a2a3e', borderRadius: '8px' }}>
              <div style={{ color: '#aaa', fontSize: '14px', lineHeight: '1.6' }}>
                <p style={{ margin: '0 0 8px 0' }}>
                  <strong style={{ color: '#fff' }}>グループモードとは？</strong>
                </p>
                <ul style={{ margin: 0, paddingLeft: '20px', marginBottom: '12px' }}>
                  <li>各グループは個別に設定したカードプールを使用</li>
                  <li>パートナーや交流は同じグループ内でのみ発生</li>
                </ul>
                <p style={{ margin: '0 0 8px 0' }}>
                  <strong style={{ color: '#fff' }}>進化モード設定</strong>
                </p>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li><strong>人口モード</strong>: 自然増減（通常の出生・死亡）/ 固定人口（リスポーンで人口維持）</li>
                  <li><strong>戦闘タイプ</strong>: 内部戦（同グループ内）と外部戦（他グループと）の有効/無効</li>
                  <li><strong>進化に反映</strong>: どの戦闘結果をデッキ進化の判断材料にするか</li>
                </ul>
                <p style={{ margin: '8px 0 0 0', color: '#888', fontSize: '12px' }}>
                  例: グループAは全カードで内部進化、グループBは制限カードで対A戦進化
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowGroupSettings(false)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#6c757d',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handleApplyGroupSettings}
                disabled={groupModeEnabled && groupSettings.slice(0, groupCount).some(s => s.allowedCardIds.size === 0)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  borderRadius: '6px',
                  border: 'none',
                  background: (groupModeEnabled && groupSettings.slice(0, groupCount).some(s => s.allowedCardIds.size === 0))
                    ? '#555'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  cursor: (groupModeEnabled && groupSettings.slice(0, groupCount).some(s => s.allowedCardIds.size === 0))
                    ? 'not-allowed'
                    : 'pointer',
                  fontWeight: 'bold',
                }}
              >
                適用してリセット
              </button>
            </div>
          </div>
        </>
      )}

      {/* グループ別カード選択モーダル */}
      {editingGroupCardIndex >= 0 && (
        <GroupCardSelectionModal
          allCards={allCards}
          allowedCardIds={groupSettings[editingGroupCardIndex].allowedCardIds}
          groupName={groupSettings[editingGroupCardIndex].name}
          groupColor={groupSettings[editingGroupCardIndex].color}
          onSave={(allowedIds) => handleSaveGroupCards(editingGroupCardIndex, allowedIds)}
          onClose={() => setEditingGroupCardIndex(-1)}
          isMobile={isMobile}
        />
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '16px',
    maxWidth: '100%',
    height: '100vh',
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  backButton: {
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    border: 'none',
    borderRadius: '6px',
    background: '#6c757d',
    color: 'white',
    fontWeight: 'bold',
  },
  exclusionButton: {
    padding: '8px 12px',
    fontSize: '14px',
    cursor: 'pointer',
    border: 'none',
    borderRadius: '6px',
    background: 'linear-gradient(135deg, #4a4a6a 0%, #3a3a5a 100%)',
    color: 'white',
    fontWeight: 'bold',
    transition: 'all 0.2s',
  },
  mainContent: {
    display: 'grid',
    gridTemplateColumns: 'minmax(400px, 500px) 1fr 320px',
    gap: '16px',
    flex: 1,
    overflow: 'hidden',
  },
  mapColumn: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  mapColumnMobile: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flex: 1,
    minHeight: 0,
  },
  centerColumn: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#1a1a2e',
    borderRadius: '8px',
    border: '2px solid #444',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  tabBarContainer: {
    display: 'flex',
    alignItems: 'stretch',
    borderBottom: '2px solid #444',
    flexShrink: 0,
    position: 'relative',
  },
  tabBar: {
    display: 'flex',
    gap: '8px',
    padding: '12px 12px 0 12px',
    flexShrink: 0,
    flex: 1,
    minWidth: 0,
    flexWrap: 'nowrap',
  },
  tabScrollButton: {
    padding: '8px 12px',
    fontSize: '14px',
    cursor: 'pointer',
    border: 'none',
    background: 'linear-gradient(135deg, #3a3a5e 0%, #2a2a3e 100%)',
    color: '#fff',
    fontWeight: 'bold',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    zIndex: 1,
  },
  tabButton: {
    padding: '10px 20px',
    fontSize: '14px',
    cursor: 'pointer',
    border: 'none',
    borderRadius: '8px 8px 0 0',
    background: '#2a2a3e',
    color: '#aaa',
    fontWeight: 'bold',
    transition: 'all 0.2s',
  },
  tabButtonActive: {
    background: '#3a3a5e',
    color: '#fff',
    borderBottom: '2px solid #667eea',
  },
  tabContent: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    zIndex: 1000,
  },
  modalContent: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1001,
    maxWidth: '800px',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  // モバイル用スタイル
  mobileViewToggle: {
    display: 'flex',
    gap: '8px',
    marginBottom: '8px',
    flexShrink: 0,
  },
  mobileViewButton: {
    flex: 1,
    padding: '10px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    border: 'none',
    borderRadius: '8px',
    background: '#2a2a3e',
    color: '#aaa',
    fontWeight: 'bold',
    transition: 'all 0.2s',
  },
  mobileViewButtonActive: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
  },
};
